import { useEffect, useState } from 'react';

import {
  CountryViewModel,
  EmployeeIdentificationType,
  EmploymentBoardDecisionLockViewModel,
  EmploymentPeriodStatusEnum,
  EmploymentViewModelNew,
} from '@fora-monorepo/employer-my-pages-common/types';
import {
  Assets,
  Box,
  Container,
  Feedback,
  Flex,
  Headings,
  Link,
  Tag,
  Text,
  useIsDeviceSize,
  VerticalSpacer,
} from '@foradevelopment/fora-ui';
import { dateStringFormatter, isFuture } from '@fora-monorepo/date-utils';

import { AddressDetails, PersonDetails } from '../EmploymentDetails';
import { MarkupChangesLocked } from '../ManageEmployment';
import { getCountryFromCode, getCurrentEmploymentPeriod } from '../../common/utils';
import { useTypedTranslation } from '../../i18n/i18n';
import { links } from '../../router/links';
import { MarkupAndParametersList } from '../EmploymentDetails/MarkupAndParametersList';
import { useAppSelector } from '../../redux/hooks';

type EmploymentTableRowExpandedContentProps = {
  employment: EmploymentViewModelNew;
  countries: CountryViewModel[];
  formattedEmployeeId: string;
  localizedIdTypeString: string;
  getHasBoardDecisionLock: () => Promise<EmploymentBoardDecisionLockViewModel | null>;
  editUrl: string;
  markupExpiringText?: string;
};

export const EmploymentTableRowExpandedContent = ({
  employment,
  countries,
  getHasBoardDecisionLock,
  markupExpiringText,
}: EmploymentTableRowExpandedContentProps) => {
  const { t } = useTypedTranslation();
  const { isMobile } = useIsDeviceSize();

  const [boardDecisionLock, setBoardDecisionLock] = useState<EmploymentBoardDecisionLockViewModel | null>(null);
  const [hasFetchedBoardDecisionLock, setHasFetchedBoardDecisionLock] = useState<boolean>(false);

  const employer = useAppSelector(state => state.employer);

  const isOnActiveAgreement = employer.employerInfo.agreementNumber === employment.foraAgreementNumber;
  const currentEmploymentPeriod = getCurrentEmploymentPeriod(employment.employmentPeriods);

  useEffect(() => {
    getHasBoardDecisionLock().then(lock => setBoardDecisionLock(lock));
    setHasFetchedBoardDecisionLock(true);
  }, [getHasBoardDecisionLock]);

  const employmentPeriodsWithOriginalIndex = employment.employmentPeriods.map((period, index) => ({
    ...period,
    index,
  }));

  const sortedEmploymentPeriods = [
    ...employmentPeriodsWithOriginalIndex.filter(
      employmentPeriod => employmentPeriod.employmentStatus === EmploymentPeriodStatusEnum.Active
    ),
    ...employmentPeriodsWithOriginalIndex.filter(
      employmentPeriod => employmentPeriod.employmentStatus === EmploymentPeriodStatusEnum.Future
    ),
    ...employmentPeriodsWithOriginalIndex.filter(
      employmentPeriod => employmentPeriod.employmentStatus === EmploymentPeriodStatusEnum.Expired
    ),
  ];

  return (
    <Flex justifyContent="center" paddingBottom={70} paddingTop={50} paddingLeft={20} paddingRight={20}>
      <Container maxWidth={578} width="100%">
        <PersonDetails
          {...employment.employee}
          policyNumber={employment.policyNumber}
          isVerified={employment.employee.isVerified}
        />
        {employment.employee.identificationType === EmployeeIdentificationType.CoOrdinationNumber &&
          !employment.employee.isVerified &&
          employment.employee.address && (
            <Container marginTop={10} marginBottom={10}>
              <AddressDetails
                title={t('common.address')}
                {...employment.employee.address}
                country={getCountryFromCode(countries, employment.employee.address.countryCode)}
                singleRow
              />
            </Container>
          )}
        {isOnActiveAgreement &&
          employment.policyNumber &&
          employment.employee.identificationType === EmployeeIdentificationType.CoOrdinationNumber &&
          !employment.employee.isVerified && (
            <Link hasIcon={false} to={`${links.editEmployee}/${employment.policyNumber}`}>
              {t('section.employeeDetails.editEmployeeLink')} <Assets.EditIcon width={14} marginLeft={6} />
            </Link>
          )}
        {currentEmploymentPeriod?.employmentEndDate && isFuture(currentEmploymentPeriod.employmentEndDate) && (
          <Container marginTop={10}>
            <Tag
              text={t('section.employeeDetails.employmentEndsTag', {
                endDate: currentEmploymentPeriod.employmentEndDate,
              })}
              variant="informational"
            />
          </Container>
        )}
        {markupExpiringText && isMobile && (
          <Container marginTop={40}>
            <Feedback type="warning" negativeSideMargins={false} marginTop={40} iconAbove={true}>
              {markupExpiringText}
            </Feedback>
          </Container>
        )}
        <Container marginTop={40}>
          {boardDecisionLock && <MarkupChangesLocked boardDecisionLock={boardDecisionLock} type="feedback" />}
          {isOnActiveAgreement && employment.isTerminated && !boardDecisionLock && (
            <Link
              to={`${links.addEmploymentPeriod}/${employment.policyNumber}`}
              styleAs="button"
              variant="outline"
              hasIcon={false}
              fullWidth={false}
            >
              <Assets.PlusIcon color="foraBlue" marginRight={8} />
              {t('section.employeeDetails.addEmplyment')}
            </Link>
          )}
          {sortedEmploymentPeriods.map((employmentPeriod, i) => (
            <Container key={i}>
              <Flex justifyContent="space-between" marginBottom={30} marginTop={60} flexDirection={['column', 'row']}>
                <Box>
                  <Headings.H4 styleAs="h3">
                    {employmentPeriod.employmentStatus === EmploymentPeriodStatusEnum.Active
                      ? t('section.employmentDetails.currentEmploymentTitle')
                      : employmentPeriod.employmentStatus === EmploymentPeriodStatusEnum.Expired
                        ? t('section.employmentDetails.previousEmploymentDetails')
                        : t('section.employmentDetails.futureEmploymentTitle')}
                  </Headings.H4>
                  <Text marginTop={6} color="black70" marginBottom={[20, 0]}>
                    {`${
                      employmentPeriod.employmentEndDate
                        ? dateStringFormatter(employmentPeriod.employmentStartDate) +
                          ' - ' +
                          employmentPeriod.employmentEndDate
                        : t('section.employmentDetails.employmentPeriodOngoing', {
                            date: dateStringFormatter(employmentPeriod.employmentStartDate),
                          })
                    }`}
                  </Text>
                </Box>
                {isOnActiveAgreement && !boardDecisionLock ? (
                  <Box>
                    <Link
                      to={`${links.editEmploymentPeriod}/${employment.policyNumber}/${employmentPeriod.index}`}
                      styleAs="button"
                      hasIcon={false}
                      state={{ boardDecisionLock, hasFetchedBoardDecisionLock }}
                      fullWidth={false}
                    >
                      {t('common.edit')}
                      <Assets.EditIcon color="white" marginLeft={8} />
                    </Link>
                  </Box>
                ) : null}
              </Flex>
              <MarkupAndParametersList
                fokParameters={employmentPeriod.fokParameterChoices}
                employmentPeriodStatus={employmentPeriod.employmentStatus}
                markups={employmentPeriod.markups}
              />
              <VerticalSpacer marginBottom={10} />
              {isOnActiveAgreement && !boardDecisionLock && employmentPeriod.canEditParameters ? (
                <Link
                  hasIcon={false}
                  to={`${links.editParameter}/${employment.policyNumber}/${employmentPeriod.index}`}
                >
                  {t('section.employmentDetails.editParameter')} <Assets.EditIcon width={14} marginLeft={6} />
                </Link>
              ) : null}
            </Container>
          ))}
        </Container>
      </Container>
    </Flex>
  );
};
